import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Terminal as TerminalIcon,
  ScrollText,
  Activity,
  Info,
  FolderTree,
  Folder,
  File as FileIcon,
  ChevronRight,
} from "lucide-react";
import {
  inspectLiveContainer,
  getContainerFiles,
  issueHostStreamTicket,
  dockerWsUrl,
} from "@/api/endpoints/docker";
import type { LiveContainer } from "@/types/docker";
import { ExecTerminal } from "./ExecTerminal";

type Tab = "inspect" | "logs" | "stats" | "terminal" | "files";

interface Props {
  hostId: number;
  container: LiveContainer;
  onClose: () => void;
}

const TABS: { key: Tab; label: string; icon: typeof Info }[] = [
  { key: "inspect", label: "Inspect", icon: Info },
  { key: "logs", label: "Logs", icon: ScrollText },
  { key: "stats", label: "Stats", icon: Activity },
  { key: "terminal", label: "Terminal", icon: TerminalIcon },
  { key: "files", label: "Files", icon: FolderTree },
];

export function ContainerDetailDrawer({ hostId, container, onClose }: Props) {
  const [tab, setTab] = useState<Tab>("inspect");

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
      <div className="flex h-full w-full max-w-3xl flex-col border-l border-gray-800 bg-gray-950 shadow-2xl">
        {/* header */}
        <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate font-mono text-sm text-gray-100">
              {container.name}
            </div>
            <div className="truncate text-xs text-gray-500">
              {container.image} · {container.short_id}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* tabs */}
        <div className="flex gap-1 border-b border-gray-800 px-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
                tab === t.key
                  ? "border-blue-500 text-blue-300"
                  : "border-transparent text-gray-400 hover:text-gray-200"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* body */}
        <div className="min-h-0 flex-1 overflow-hidden p-3">
          {tab === "inspect" && (
            <InspectPane hostId={hostId} container={container} />
          )}
          {tab === "logs" && <LogsPane hostId={hostId} cid={container.id} />}
          {tab === "stats" && <StatsPane hostId={hostId} cid={container.id} />}
          {tab === "terminal" && (
            <ExecTerminal hostId={hostId} cid={container.id} />
          )}
          {tab === "files" && <FilesPane hostId={hostId} cid={container.id} />}
        </div>
      </div>
    </div>
  );
}

// --- Inspect ---------------------------------------------------------------

function InspectPane({
  hostId,
  container,
}: {
  hostId: number;
  container: LiveContainer;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["docker-inspect", hostId, container.id],
    queryFn: () => inspectLiveContainer(hostId, container.id),
  });

  return (
    <div className="h-full space-y-4 overflow-auto text-sm">
      <Section title="Résumé">
        <Field k="État" v={`${container.state ?? container.status}`} />
        {container.health && <Field k="Santé" v={container.health} />}
        <Field k="Image" v={container.image} />
        {container.command && (
          <Field k="Commande" v={container.command.join(" ")} />
        )}
        <Field k="Redémarrages" v={String(container.restart_count)} />
        {container.compose_project && (
          <Field
            k="Compose"
            v={`${container.compose_project} / ${container.compose_service ?? ""}`}
          />
        )}
        <Field k="Géré par AlphaProx" v={container.managed ? "oui" : "non"} />
      </Section>

      {container.ports.length > 0 && (
        <Section title="Ports">
          <div className="space-y-1 font-mono text-xs">
            {container.ports.map((p, i) => (
              <div key={i} className="text-gray-300">
                {p.host_ip || p.host_port
                  ? `${p.host_ip ?? "0.0.0.0"}:${p.host_port} → `
                  : ""}
                {p.container_port}/{p.protocol}
              </div>
            ))}
          </div>
        </Section>
      )}

      {Object.keys(container.labels).length > 0 && (
        <Section title="Labels">
          <div className="space-y-0.5 font-mono text-xs">
            {Object.entries(container.labels).map(([k, v]) => (
              <div key={k} className="break-all text-gray-400">
                <span className="text-gray-300">{k}</span>={v}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Inspect brut">
        {isLoading && <p className="text-gray-500">Chargement…</p>}
        {error && <p className="text-red-400">Erreur de récupération</p>}
        {data && (
          <pre className="max-h-80 overflow-auto rounded bg-gray-900 p-2 font-mono text-[11px] text-gray-400">
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h4>
      <div className="rounded-lg border border-gray-800 p-3">{children}</div>
    </div>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2 py-0.5">
      <span className="w-40 shrink-0 text-gray-500">{k}</span>
      <span className="break-all text-gray-200">{v}</span>
    </div>
  );
}

// --- Logs ------------------------------------------------------------------

function LogsPane({ hostId, cid }: { hostId: number; cid: string }) {
  const [lines, setLines] = useState<string[]>([]);
  const [status, setStatus] = useState("connexion…");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let cancelled = false;
    (async () => {
      try {
        const ticket = await issueHostStreamTicket(hostId, cid, "logs");
        if (cancelled) return;
        ws = new WebSocket(
          dockerWsUrl(
            `/docker/hosts/${hostId}/live/containers/${cid}/logs`,
            ticket,
          ),
        );
        ws.onopen = () => setStatus("connecté");
        ws.onmessage = (e) =>
          setLines((p) => [...p.slice(-800), e.data as string]);
        ws.onclose = () => setStatus("fermé");
        ws.onerror = () => setStatus("erreur");
      } catch {
        setStatus("erreur ticket");
      }
    })();
    return () => {
      cancelled = true;
      ws?.close();
    };
  }, [hostId, cid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [lines]);

  return (
    <div className="flex h-full flex-col">
      <div className="pb-1 text-xs text-gray-500">Logs · {status}</div>
      <div className="min-h-0 flex-1 overflow-auto rounded bg-gray-900 p-2 font-mono text-xs text-gray-300">
        {lines.map((l, i) => (
          <div key={i} className="whitespace-pre-wrap">
            {l}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// --- Stats -----------------------------------------------------------------

interface StatFrame {
  cpu_percent: number;
  mem_usage: number;
  mem_limit: number;
}

function mb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(0)} Mo`;
}

function StatsPane({ hostId, cid }: { hostId: number; cid: string }) {
  const [frame, setFrame] = useState<StatFrame | null>(null);
  const [cpuHistory, setCpuHistory] = useState<number[]>([]);
  const [status, setStatus] = useState("connexion…");

  useEffect(() => {
    let ws: WebSocket | null = null;
    let cancelled = false;
    (async () => {
      try {
        const ticket = await issueHostStreamTicket(hostId, cid, "stats");
        if (cancelled) return;
        ws = new WebSocket(
          dockerWsUrl(
            `/docker/hosts/${hostId}/live/containers/${cid}/stats`,
            ticket,
          ),
        );
        ws.onopen = () => setStatus("connecté");
        ws.onmessage = (e) => {
          try {
            const f = JSON.parse(e.data as string) as StatFrame;
            setFrame(f);
            setCpuHistory((p) => [...p.slice(-59), f.cpu_percent]);
          } catch {
            /* ignore malformed frame */
          }
        };
        ws.onclose = () => setStatus("fermé");
        ws.onerror = () => setStatus("erreur");
      } catch {
        setStatus("erreur ticket");
      }
    })();
    return () => {
      cancelled = true;
      ws?.close();
    };
  }, [hostId, cid]);

  const memPct =
    frame && frame.mem_limit > 0
      ? (frame.mem_usage / frame.mem_limit) * 100
      : 0;
  const max = Math.max(1, ...cpuHistory);

  return (
    <div className="space-y-4 text-sm">
      <div className="text-xs text-gray-500">Stats · {status}</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-gray-800 p-3">
          <div className="text-xs text-gray-500">CPU</div>
          <div className="text-2xl font-semibold text-gray-100">
            {frame ? frame.cpu_percent.toFixed(1) : "—"}%
          </div>
        </div>
        <div className="rounded-lg border border-gray-800 p-3">
          <div className="text-xs text-gray-500">Mémoire</div>
          <div className="text-2xl font-semibold text-gray-100">
            {frame ? mb(frame.mem_usage) : "—"}
          </div>
          <div className="text-xs text-gray-500">
            {frame ? `${memPct.toFixed(0)}% de ${mb(frame.mem_limit)}` : ""}
          </div>
        </div>
      </div>
      {/* CPU sparkline */}
      <div className="flex h-20 items-end gap-0.5 rounded-lg border border-gray-800 p-2">
        {cpuHistory.map((v, i) => (
          <div
            key={i}
            className="flex-1 rounded-sm bg-blue-600/70"
            style={{ height: `${(v / max) * 100}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// --- Files -----------------------------------------------------------------

function FilesPane({ hostId, cid }: { hostId: number; cid: string }) {
  const [path, setPath] = useState("/");
  const { data, isLoading, error } = useQuery({
    queryKey: ["docker-files", hostId, cid, path],
    queryFn: () => getContainerFiles(hostId, cid, path),
  });

  const segments = path.split("/").filter(Boolean);

  return (
    <div className="flex h-full flex-col text-sm">
      {/* breadcrumb */}
      <div className="flex flex-wrap items-center gap-1 pb-2 font-mono text-xs text-gray-400">
        <button onClick={() => setPath("/")} className="hover:text-gray-100">
          /
        </button>
        {segments.map((seg, i) => {
          const p = "/" + segments.slice(0, i + 1).join("/");
          return (
            <span key={p} className="flex items-center gap-1">
              <ChevronRight className="h-3 w-3 text-gray-600" />
              <button onClick={() => setPath(p)} className="hover:text-gray-100">
                {seg}
              </button>
            </span>
          );
        })}
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-800">
        {isLoading && <p className="p-3 text-gray-500">Chargement…</p>}
        {error && (
          <p className="p-3 text-red-400">
            Impossible de lister ce dossier (pas de shell ?).
          </p>
        )}
        {data?.entries.map((e) => (
          <button
            key={e.name}
            disabled={!e.is_dir}
            onClick={() =>
              e.is_dir &&
              setPath(`${path.replace(/\/$/, "")}/${e.name}`.replace("//", "/"))
            }
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left font-mono text-xs ${
              e.is_dir
                ? "text-blue-300 hover:bg-gray-900"
                : "cursor-default text-gray-400"
            }`}
          >
            {e.is_dir ? (
              <Folder className="h-4 w-4 shrink-0" />
            ) : (
              <FileIcon className="h-4 w-4 shrink-0 text-gray-600" />
            )}
            {e.name}
          </button>
        ))}
        {data && data.entries.length === 0 && (
          <p className="p-3 text-gray-500">Dossier vide.</p>
        )}
      </div>
    </div>
  );
}
